// @flow

import React, { useState, useEffect, useRef } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { __ } from '../../../i18n/l10n';

import { sizes, media, fontFamily, colorPalette } from '../../../styles/GlobalStyle';
import { Button } from './Button';
import { Icon } from './Icon';

type InputGenderProps = {|
  className?: string,
  value: string,
  name: string,
  label: string,
  id: string,
  invalid?: boolean,
  register?: void,
  onChange?: (event: string) => any
|};

const InputGenderRaw = ({
  className,
  value,
  name,
  label,
  id,
  invalid = false,
  register,
  onChange
}: InputGenderProps) => {
  const [formattedValue, setFormattedValue] = useState<string>('');
  const label1 = useRef();
  const label2 = useRef();

  const updateGender = (newGender, labelRef) => {
    if (labelRef.current) {
      const { current } = labelRef;
      current.focus();
      current.click();
    }

    if (onChange && formattedValue !== newGender) {
      onChange(newGender);
    }

    setFormattedValue(newGender);
  };

  useEffect(() => {
    if (value !== '') {
      setFormattedValue(value);
    }
  }, [value]);

  return (
    <span className={`${className || ''}${invalid ? ' invalid' : ''}`}>
      <span className="label">{label}</span>
      <span className="choices-wrapper">
        <label className="choice" htmlFor={`${id}F`} ref={label1}>
          <Button
            id={`${id}FButton`}
            onClick={() => updateGender('F', label1)}
            theme={formattedValue === 'F' ? 'primary' : 'secondary'}
          >
            {__('Female')}
            <Icon name="female" />
          </Button>
          <input
            className="input-radio"
            type="radio"
            name={name}
            id={`${id}F`}
            ref={register}
            value="F"
            tabIndex="-1"
          />
        </label>
        <label className="choice" htmlFor={`${id}M`} ref={label2}>
          <Button
            id={`${id}MButton`}
            onClick={() => updateGender('M', label2)}
            theme={formattedValue === 'M' ? 'primary' : 'secondary'}
          >
            {__('Male')} <Icon name="male" />
          </Button>
          <input
            className="input-radio"
            type="radio"
            name={name}
            id={`${id}M`}
            ref={register}
            value="M"
            tabIndex="-1"
          />
        </label>
      </span>
    </span>
  );
};

export const InputGender: StyledComponent<InputGenderProps, {}, typeof InputGenderRaw> = styled(InputGenderRaw)`
  position: relative;
  display: inline-block;
  width: 100%;
  padding-top: ${sizes.padding};
  font-weight: ${fontFamily.primary.black.fontWeight};

  .input-radio {
    position: absolute;
    left: -10000px;
    opacity: 0;
  }

  .label {
    display: block;
    text-transform: uppercase;
    margin-bottom: calc(${sizes.padding} / 2);
  }

  .choices-wrapper {
    display: flex;
    width: 100%;
  }

  .choice {
    position: relative;
    width: 100%;
    margin-right: calc(${sizes.padding} / 4);

    &:after {
      display: block;
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      height: 2px;
      z-index: 1;
      background: ${colorPalette.danger.active};
      width: 0;
      transition: all 0.6s;
      opacity: 0;
    }

    &:last-child {
      margin-right: 0;
    }
  }

  &.invalid {
    .label {
      color: ${colorPalette.danger.active};
    }

    .choice:after {
      width: 100%;
      opacity: 1;
    }
  }

  ${media.greaterThan('desktop')`
    .choice {
      margin-right: calc(${sizes.padding} / 2);
    }
  `}
`;
